(() => {
  if (customElements.get('td-mega-menu')) {
    return;
  }

  class TdMegaMenu extends HTMLElement {
    #section;
    #triggers;
    #panels;
    #closeTimer;
    #cleanupTimer;
    #switchTimer;
    #contentDuration = 420;
    #switchDuration = 280;
    #activePanelId = null;
    #animationDuration = 1200;

    connectedCallback() {
      if (!window.matchMedia('(min-width: 1000px)').matches) {
        return;
      }

      this.#contentDuration = Math.round(this.#animationDuration * 0.35);
      this.#switchDuration = Math.round(this.#contentDuration * 0.85);
      this.style.setProperty('--td-mega-menu-duration', `${this.#animationDuration}ms`);
      this.style.setProperty('--td-mega-menu-content-delay', `${Math.round(this.#animationDuration * 0.45)}ms`);
      this.style.setProperty('--td-mega-menu-content-duration', `${this.#contentDuration}ms`);
      this.style.setProperty('--td-mega-menu-switch-duration', `${this.#switchDuration}ms`);

      this.#section = this.closest('.shopify-section--header');
      if (!this.#section) {
        return;
      }

      this.#triggers = [...this.#section.querySelectorAll('.td-mega-menu__trigger')];
      this.#panels = [...this.querySelectorAll('.td-mega-menu__panel')];

      if (this.#triggers.length === 0 || this.#panels.length === 0) {
        return;
      }

      this.#triggers.forEach((trigger) => {
        trigger.addEventListener('mouseenter', () => this.#open(trigger));
        trigger.addEventListener('focus', () => this.#open(trigger));
        trigger.addEventListener('click', () => {
          if (trigger.tagName !== 'BUTTON') {
            return;
          }

          const panelId = trigger.getAttribute('aria-controls');
          const isOpen =
            panelId &&
            panelId === this.#activePanelId &&
            this.#section.classList.contains('td-mega-menu-open') &&
            !this.classList.contains('is-closing');

          if (isOpen) {
            this.#close();
            return;
          }

          this.#open(trigger);
        });
      });

      const headerEl = this.#section.querySelector('x-header.header');

      headerEl?.addEventListener('mouseleave', (event) => {
        if (!this.contains(event.relatedTarget)) {
          this.#scheduleClose();
        }
      });

      headerEl?.addEventListener('mouseenter', () => this.#clearCloseTimer());

      this.addEventListener('mouseenter', () => this.#clearCloseTimer());

      this.addEventListener('mouseleave', (event) => {
        if (!headerEl?.contains(event.relatedTarget)) {
          this.#scheduleClose();
        }
      });

      const onFocusOut = (event) => {
        const next = event.relatedTarget;

        if (next && (this.#section.contains(next) || this.contains(next))) {
          return;
        }

        window.setTimeout(() => {
          const active = document.activeElement;

          if (active && (this.#section.contains(active) || this.contains(active))) {
            return;
          }

          if (!this.#section.classList.contains('td-mega-menu-open')) {
            return;
          }

          this.#close();
        }, 0);
      };

      this.#section.addEventListener('focusout', onFocusOut);
      this.addEventListener('focusout', onFocusOut);

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          this.#close();
        }
      });
    }

    #clearCloseTimer() {
      window.clearTimeout(this.#closeTimer);
      this.#closeTimer = undefined;
    }

    #clearCleanupTimer() {
      window.clearTimeout(this.#cleanupTimer);
      this.#cleanupTimer = undefined;
    }

    #clearSwitchTimer() {
      window.clearTimeout(this.#switchTimer);
      this.#switchTimer = undefined;
    }

    #scheduleClose() {
      this.#clearCloseTimer();
      this.#closeTimer = window.setTimeout(() => this.#close(), 120);
    }

    #open(trigger) {
      const panelId = trigger.getAttribute('aria-controls');
      if (!panelId) {
        return;
      }

      if (panelId === this.#activePanelId) {
        this.#clearCloseTimer();
        return;
      }

      this.#clearCloseTimer();
      this.#clearCleanupTimer();
      this.#clearSwitchTimer();
      this.classList.remove('is-closing');

      const previousPanelId = this.#activePanelId;
      const isSwitch =
        previousPanelId &&
        this.#section.classList.contains('td-mega-menu-open');

      if (isSwitch) {
        this.#switchPanel(previousPanelId, panelId);
      } else {
        this.classList.remove('is-switching');
        this.#panels.forEach((panel) => {
          panel.classList.remove('is-leaving');
          panel.classList.toggle('is-active', panel.id === panelId);
        });
      }

      this.#activePanelId = panelId;

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.#section.classList.add('td-mega-menu-open');
        });
      });

      this.#triggers.forEach((item) => {
        const isActive = item === trigger;
        item.setAttribute('aria-expanded', isActive ? 'true' : 'false');
        item.classList.toggle('is-active', isActive);
      });
    }

    #switchPanel(fromPanelId, toPanelId) {
      this.classList.add('is-switching');

      this.#panels.forEach((panel) => {
        panel.classList.remove('is-leaving', 'is-active');
      });

      const outgoing = this.#panels.find((panel) => panel.id === fromPanelId);
      const incoming = this.#panels.find((panel) => panel.id === toPanelId);

      outgoing?.classList.add('is-leaving');
      incoming?.classList.add('is-active');

      this.#switchTimer = window.setTimeout(() => {
        outgoing?.classList.remove('is-leaving');
        this.classList.remove('is-switching');
      }, this.#switchDuration);
    }

    #close() {
      if (this.classList.contains('is-closing')) {
        return;
      }

      this.#clearCloseTimer();
      this.#clearSwitchTimer();
      this.#activePanelId = null;
      this.classList.add('is-closing');
      this.classList.remove('is-switching');

      this.#clearCleanupTimer();
      this.#cleanupTimer = window.setTimeout(() => {
        if (!this.classList.contains('is-closing')) {
          return;
        }

        this.#section?.classList.remove('td-mega-menu-open');

        this.#cleanupTimer = window.setTimeout(() => {
          if (this.#section?.classList.contains('td-mega-menu-open')) {
            return;
          }

          this.classList.remove('is-closing');

          this.#triggers?.forEach((item) => {
            item.setAttribute('aria-expanded', 'false');
            item.classList.remove('is-active');
          });

          this.#panels?.forEach((panel) => {
            panel.classList.remove('is-active', 'is-leaving');
          });
        }, this.#animationDuration);
      }, this.#contentDuration);
    }
  }

  customElements.define('td-mega-menu', TdMegaMenu);
})();
